import React from 'react';
import { CalendarOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderStepContext } from '../orderStepContext';
import { Callout, InfoList, StepSection } from '../OrderProcessParts';
import { OrderStockTable } from '../OrderProcessTables';
import { formatDateEuropean } from '../../../../utils/dateUtils';
import { truckLoadingDate } from '../../../../utils/truckDates';
import { getTransportStatusDisplayTranslationKey } from '../../../transports/transportRouteViews';
import OrderSchedule from '../OrderSchedule';
import OrderTransportSummary, { activeOrderTransports } from '../OrderTransportSummary';
import OrderDeliveryScheduleSection from '../OrderDeliveryScheduleSection';
import OrderSummaryCard from '../OrderSummaryCard';
import OrderMarginCard from '../guide/OrderMarginCard';
import { getOrderTransportMissing, getOrderWaitingItems } from '../orderProcessFacts';

/**
 * „Transport do klienta” in one phrase: straight from the factory — the truck of
 * the draft ZD (to the customer, or to the warehouse, or not planned yet); else
 * self-pickup, linked trucks or still to plan.
 */
export function useDeliverySummary(ctx: OrderStepContext): string {
  const { t } = useTranslation(['orders']);
  if (ctx.order.fulfillmentSource === 'FACTORY_DIRECT') {
    const trucks = (ctx.stockStatus?.supplierDraft.transports ?? []);
    if (!trucks.length) return t('orders:process.transport.factoryNone');
    const describe = (truck: typeof trucks[number]) => [
      truck.company,
      truckLoadingDate(truck) ? formatDateEuropean(truckLoadingDate(truck)) : null,
      truck.status ? t(getTransportStatusDisplayTranslationKey(truck.status)) : null,
    ].filter(Boolean).join(', ');
    const toCustomer = trucks.filter((truck) => truck.toCustomer);
    return toCustomer.length
      ? t('orders:process.transport.factoryToCustomer', { details: toCustomer.map(describe).join(' · ') })
      : t('orders:process.transport.factoryToWarehouse', { details: trucks.map(describe).join(' · ') });
  }
  if (ctx.order.deliveryMode === 'CUSTOMER_PICKUP') return t('orders:process.transport.pickup');
  const trucks = activeOrderTransports(ctx.transports);
  if (!ctx.caps.canViewTransports) return t('orders:process.transport.ourDelivery');
  return trucks.length
    ? t('orders:process.transport.linked', { count: trucks.length })
    : t('orders:process.transport.toPlan');
}

/**
 * „Na co czeka zamówienie” in one sentence: what really remains
 * (`getOrderWaitingItems`: ZD to place / review / send, PZ, outgoing truck),
 * or that everything is on the warehouse.
 */
function useWaitingHint(ctx: OrderStepContext): string {
  const { t } = useTranslation(['orders']);
  if (ctx.stockStatus && !ctx.facts.missing.length) return t('orders:process.fulfillment.waitingHintReady');
  const transportMissing = getOrderTransportMissing(ctx.order, ctx.caps.canViewTransports, ctx.transports);
  const items = getOrderWaitingItems({ stockStatus: ctx.stockStatus, missing: ctx.facts.missing, transportMissing });
  if (!items.length) return t('orders:process.fulfillment.waitingHint');
  return t('orders:process.fulfillment.waitingFor', { items: items.map((item) => t(item.key, item.params)).join(', ') });
}

/**
 * W realizacji (makieta `sReal`): with full FIFO the goods leave from the
 * warehouse once the missing ones are received (PZ). Per line:
 * ordered, on hand, state; the draft ZD; source always „Magazyn (FIFO)”.
 * Below, „Marża zamówienia” (the steps 1–3 card) for full-margin readers
 * only (ADMIN, owner 2026-10-06); Handlowiec and Operacje get no card here.
 * Missing goods and the shortage notice to Operacje are said once, by the step
 * bar (`orderStepBar` missingHint ← `shortageNoticeFor`), not repeated here.
 */
export default function FulfillmentStep({
  ctx,
  onOpenTransportLink,
  onDeliveryScheduleChanged,
  onTransportStatusChange,
  onUnlinkTransport,
}: {
  ctx: OrderStepContext;
  onOpenTransportLink?: () => void;
  onDeliveryScheduleChanged?: () => void;
  /** „Zmień status” of a truck (transport managers only). */
  onTransportStatusChange?: React.ComponentProps<typeof OrderDeliveryScheduleSection>['onStatusChange'];
  /** „Odepnij od zamówienia” (roles allowed to unlink only). */
  onUnlinkTransport?: (transportId: string) => Promise<void> | void;
}) {
  const { t } = useTranslation(['orders']);
  const delivery = useDeliverySummary(ctx);
  const waitingHint = useWaitingHint(ctx);
  // With the „Transporty” table (forecast by delivery, 2026-10-16) the trucks
  // are listed there; the read-only summary stays for readers without it.
  const schedule = ctx.deliverySchedule ?? null;
  const trucks = schedule ? [] : activeOrderTransports(ctx.transports);
  const hasTrucks = schedule ? schedule.transports.length > 0 : trucks.length > 0;
  const factoryDirect = ctx.order.fulfillmentSource === 'FACTORY_DIRECT';
  return (
    <div className="zk-stack" data-testid="zk-step-FULFILLMENT">
      <StepSection
        title={t('orders:process.fulfillment.waitingTitle')}
        icon={<InboxOutlined />}
        hint={waitingHint}
      >
        <OrderStockTable stockStatus={ctx.stockStatus} products={ctx.order.productsLoaded ? ctx.order.products : []} />
        {ctx.stockStatus && !ctx.facts.missing.length && (
          <Callout tone="success">{t('orders:process.fulfillment.allInStock')}</Callout>
        )}
      </StepSection>
      {schedule && (
        <OrderDeliveryScheduleSection
          schedule={schedule}
          transports={ctx.caps.canViewTransports ? ctx.transports : []}
          canViewCosts={ctx.caps.canViewTransportCosts}
          products={ctx.order.productsLoaded ? ctx.order.products : []}
          onStatusChange={onTransportStatusChange}
          onUnlink={onUnlinkTransport}
          readOnly={ctx.readOnly}
          canOpenTransports={ctx.caps.canViewTransports}
          canAddTransport={ctx.caps.canManageTransports && !factoryDirect && ctx.order.deliveryMode !== 'CUSTOMER_PICKUP'}
          onOpenTransportLink={onOpenTransportLink}
          onChanged={onDeliveryScheduleChanged}
        />
      )}
      <div className="zk-grid zk-grid--even">
        <div className="zk-col">
          <OrderSummaryCard ctx={ctx} />
        </div>
        <div className="zk-col">
          <StepSection title={t('orders:process.schedule.title')} icon={<CalendarOutlined />} testId="zk-schedule-card">
            <OrderSchedule ctx={ctx} />
            <h4 className="zk-section__group-title">{t('orders:process.fulfillment.infoTitle')}</h4>
            {/* Linked trucks are listed in „Transporty i terminy dostarczenia” (or,
                without that table, right below in OrderTransportSummary), so the
                one-phrase „Transport do klienta” row is shown only without them. */}
            <InfoList rows={[
              [t('orders:process.fulfillment.source'), t(factoryDirect ? 'orders:process.fulfillment.sourceFactory' : 'orders:process.fulfillment.sourceWarehouse')],
              ...(hasTrucks ? [] : [[t('orders:process.fulfillment.delivery'), delivery] as [string, string]]),
            ]} />
            {trucks.length > 0 && <OrderTransportSummary transports={trucks} canViewCosts={ctx.caps.canViewTransportCosts} />}
          </StepSection>
        </div>
      </div>
      {ctx.caps.canViewMargin && <OrderMarginCard ctx={ctx} />}
    </div>
  );
}
